import { type ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { RouteConfig } from '@/routes/types';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/use-auth-store';
import { FullScreenLoader } from '@/components/ui/loader';
import { useQuery } from '@tanstack/react-query';
import { subscriptionService } from '@/services/subscription/subscription.service';
import { useOnboardingStatus } from '@/hooks';
import { useCompanyStore } from '@/store/use-company-store';
import { companyOnboardingService } from '@/services/company-onboarding/company-onboarding.service';
import { useUserMemberships } from '@/hooks/company-membership/use-company-membership';

interface RouteGuardProps {
  route: RouteConfig;
  children: ReactNode;
}

export const RouteGuard = ({
  route,
  children
}: RouteGuardProps) => {
  const { isAuthenticated, isAuthLoading, roles } = useAuthStore();
  const { t } = useTranslation();
  const location = useLocation();
  const { currentCompanyId } = useCompanyStore();

  const requiresAuth = route.meta?.requiresAuth !== false;
  const requiresSubscription = requiresAuth && route.meta?.requiresSubscription !== false;

  if (isAuthLoading && requiresAuth) {
    return <FullScreenLoader label={t('common.checkingAuthentication')} />;
  }

  if (requiresAuth && !isAuthenticated) {
    const redirectPath = location.pathname + location.search;
    return <Navigate to={`/auth/login?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  }

  if (route.meta?.roles && route.meta.roles.length > 0) {
    const hasRequiredRole = route.meta.roles.some(role =>
      roles?.some(userRole => userRole === role)
    );

    if (!hasRequiredRole) {
      return <Navigate to="/" replace />;
    }
  }

  // Get user memberships to check if they should skip onboarding
  // Only fetch when authenticated
  const { data: memberships } = useUserMemberships({
    enabled: isAuthenticated
  });
  const hasActiveMemberships = (memberships?.length ?? 0) > 0;

  // Ensure company context is set before making API calls
  useEffect(() => {
    if (isAuthenticated && !currentCompanyId && memberships && memberships.length > 0) {
      const { refreshMemberships } = useCompanyStore.getState();
      refreshMemberships();
    }
  }, [isAuthenticated, currentCompanyId, memberships]);

  // Check onboarding status (profile completion) for all authenticated users
  // This is now only for profile completion, not company/payment
  const { data: onboardingStatus } = useOnboardingStatus();
  const wasInvited = onboardingStatus?.wasInvited === true;
  const profileComplete = onboardingStatus?.profileComplete ?? false;

  const subCheckEnabled = isAuthenticated && requiresSubscription && !!currentCompanyId;
  const { data: subscription, isLoading: isSubLoading, error: subscriptionError } = useQuery({
    queryKey: ['subscription', 'me', currentCompanyId],
    queryFn: () => subscriptionService.getMySubscription(),
    enabled: subCheckEnabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // Check if current route should be accessible during incomplete company onboarding
  const isUserOnboardingRoute = location.pathname === '/onboarding' || location.pathname.startsWith('/onboarding/');
  const isCreateCompanyRoute = location.pathname === '/create-company';
  const isAuthRoute = location.pathname.startsWith('/auth');

  // Check company onboarding status if user has an active company and route requires auth
  // Exclude create-company route from this check to prevent redirects during company creation
  const companyOnboardingCheckEnabled = isAuthenticated && !!currentCompanyId && requiresAuth && !wasInvited && !isCreateCompanyRoute;
  const { data: companyOnboardingStatus, isLoading: isCompanyOnboardingLoading } = useQuery({
    queryKey: ["companyOnboardingStatus", currentCompanyId],
    queryFn: () => companyOnboardingService.getStatus(currentCompanyId!),
    enabled: companyOnboardingCheckEnabled,
    retry: false,
  });

  // Check if user is in the middle of creating a company
  const isCreatingCompany = () => {
    try {
      const stored = localStorage.getItem('company-creation-progress');
      return !!stored;
    } catch {
      return false;
    }
  };

  // Get if user is an admin
  const isAdmin = roles?.includes('Admin');

  // Check profile completion first - redirect to onboarding if profile incomplete
  // Even invited users need to complete their profile
  // Admin users bypass this check
  if (
    isAuthenticated &&
    !isAdmin &&
    !isAuthRoute &&
    !isUserOnboardingRoute &&
    !isCreateCompanyRoute &&
    onboardingStatus &&
    !profileComplete
  ) {
    const redirectPath = location.pathname + location.search;
    return <Navigate to={`/onboarding?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  }

  // Block access if user is creating a company (redirect to create-company page)
  // Admin users bypass this check
  if (
    isAuthenticated &&
    !isAdmin &&
    !isAuthRoute &&
    !isUserOnboardingRoute &&
    !isCreateCompanyRoute &&
    isCreatingCompany()
  ) {
    return <Navigate to="/create-company" replace />;
  }

  // Block access if company onboarding is incomplete (except for user onboarding, create company, and auth routes)
  // Redirect to create-company page instead of old onboarding route
  // Admin users bypass this check.
  //
  // Only act on a well-formed status object: handleApiRequest returns the error
  // body (not a throw) for non-401 responses, so a 403 ("not a member of this
  // company") arrives here as { statusCode, message } with no `isComplete`.
  // Treating that as "incomplete" would trap a paid owner whose membership row
  // is missing in a /create-company redirect loop, so an unknown shape is
  // treated as complete. Members are also exempt (mirrors the subscription
  // check below): being in the system already means onboarding is done.
  const companyOnboardingComplete =
    typeof companyOnboardingStatus?.isComplete === 'boolean'
      ? companyOnboardingStatus.isComplete
      : true;
  if (
    isAuthenticated &&
    !isAdmin &&
    currentCompanyId &&
    !isAuthRoute &&
    !isUserOnboardingRoute &&
    !isCreateCompanyRoute &&
    !companyOnboardingComplete &&
    !wasInvited &&
    !hasActiveMemberships
  ) {
    // Redirect to create-company page with companyId parameter
    return <Navigate to={`/create-company?companyId=${currentCompanyId}`} replace />;
  }

  if (requiresSubscription && isAuthenticated && !isAdmin) {
    if (isSubLoading || (companyOnboardingCheckEnabled && isCompanyOnboardingLoading)) {
      return <FullScreenLoader label={t('common.checkingSubscription')} />;
    }

    const hasActiveSubscription = subscription && subscription.status === 'ACTIVE';
    const subscriptionNotFound = subscriptionError && (subscriptionError as any)?.response?.status === 404;

    // Don't redirect users with memberships to onboarding - they're already part of the system
    // The backend now returns the company owner's subscription for invited users
    // If subscription is not found (404) and user has memberships, allow access anyway
    if (!hasActiveSubscription && !hasActiveMemberships && !wasInvited) {
      // Check if user has a company that needs onboarding
      if (currentCompanyId) {
        // Redirect to create-company if company exists but no subscription
        const redirectPath = location.pathname + location.search;
        const createCompanyPath = `/create-company?companyId=${currentCompanyId}`;

        // Don't add redirect parameter if redirect path is already the create-company page (prevents loops)
        const normalizedRedirectPath = redirectPath.split('?')[0]; // Remove query params for comparison
        if (normalizedRedirectPath === '/create-company') {
          return <Navigate to={createCompanyPath} replace />;
        }

        return <Navigate to={`${createCompanyPath}&redirect=${encodeURIComponent(redirectPath)}`} replace />;
      } else {
        // No company - redirect to create-company if profile is complete, otherwise onboarding
        const redirectPath = location.pathname + location.search;
        if (profileComplete) {
          return <Navigate to={`/create-company?redirect=${encodeURIComponent(redirectPath)}`} replace />;
        } else {
          return <Navigate to={`/onboarding?redirect=${encodeURIComponent(redirectPath)}`} replace />;
        }
      }
    }
  }

  return <>{children}</>;
};